import React, { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import Header from "../components/Header";
import ListaPremium, { iniciaisDe } from "../components/ListaPremium";
import { useAuth } from "../context/AuthContext";
import { Button, Field, Input, Select, Textarea, Tag, Modal, tomDoStatus, dataBR, dataHoraBR, paraInputData, brl } from "../components/ui";

const FORM_VAZIO = {
    id_cliente: "",
    data_inicio: "",
    tipo_servico: "",
    status_contrato: "em andamento",
    descricao: "",
    valor: "",
    ids_advogados: []
};

const TIPOS_SERVICO = [
    "civil", "trabalhista", "previdenciario", "criminal", "consumidor",
    "assessoria e consultoria", "acompanhamentos", "correspondencia juridica"
];
const STATUS_CONTRATO = ["em andamento", "ganho", "perdido", "cancelado"];


const Contratos = () => {
    const { podeEditar, usuario } = useAuth();
    const [busca, setBusca] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
    const [contratos, setContratos] = useState([]);
    const [detalhes, setDetalhes] = useState({ documentos: [], pagamentos: [], compromissos: [], advogados: [] });
    const [codSelecionado, setCodSelecionado] = useState(null);
    const [contratoSelecionado, setContratoSelecionado] = useState(null);
    const [mostrarInfo, setMostrarInfo] = useState(false);
    const [mostrarForm, setMostrarForm] = useState(false);
    const [editando, setEditando] = useState(false);
    const [mostrarAddDoc, setMostrarAddDoc] = useState(false);
    const [clientes, setClientes] = useState([]);
    const [equipe, setEquipe] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formData, setFormData] = useState(FORM_VAZIO);

    const carregarContratos = useCallback(async () => {
        try {
            const response = await api.get("/contratos");
            setContratos(response.data);
        } catch (error) {
            console.error("Erro ao buscar contratos:", error);
        }
    }, []);

    useEffect(() => {
        carregarContratos();
        api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
        api.get("/usuarios")
            .then((r) => setEquipe(r.data.filter((u) => u.papel !== "estagiario" && u.ativo)))
            .catch(() => {});
    }, [carregarContratos]);

    useEffect(() => {
        if (!codSelecionado) return;
        api.get(`/contratos/${codSelecionado}`)
            .then((r) => setDetalhes({
                documentos: r.data.documentos, pagamentos: r.data.pagamentos,
                compromissos: r.data.compromissos, advogados: r.data.advogados
            }))
            .catch((error) => console.error("Erro ao buscar detalhes:", error));
    }, [codSelecionado]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const toggleAdvogado = (id) => {
        setFormData((prev) => ({
            ...prev,
            ids_advogados: prev.ids_advogados.includes(id)
                ? prev.ids_advogados.filter((x) => x !== id)
                : [...prev.ids_advogados, id]
        }));
    };

    const abrirNovo = () => {
        setEditando(false);
        setFormData(FORM_VAZIO);
        setErro(null);
        setMostrarForm(true);
    };

    const abrirEditar = () => {
        setEditando(true);
        setFormData({
            id_cliente: contratoSelecionado.id_cliente,
            data_inicio: paraInputData(contratoSelecionado.data_inicio),
            tipo_servico: contratoSelecionado.tipo_servico,
            status_contrato: contratoSelecionado.status_contrato,
            descricao: contratoSelecionado.descricao || "",
            valor: contratoSelecionado.valor,
            ids_advogados: contratoSelecionado.ids_advogados || []
        });
        setErro(null);
        setMostrarInfo(false);
        setMostrarForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setErro(null);
        try {
            if (formData.ids_advogados.length === 0) {
                throw new Error("Selecione pelo menos um advogado para o contrato.");
            }
            // Um associado só enxerga os contratos em que está designado: se ele
            // se tirar da lista, o contrato some da tela dele na hora.
            if (usuario?.papel === "associado" && !formData.ids_advogados.includes(usuario.id_usuario)) {
                const confirmar = window.confirm(
                    "Você não está entre os advogados designados neste contrato. " +
                    "Ao salvar, ele deixará de aparecer para você. Continuar?"
                );
                if (!confirmar) return;
            }
            if (editando) {
                await api.put(`/contratos/${codSelecionado}`, formData);
            } else {
                await api.post("/contratos", formData);
            }
            setMostrarForm(false);
            carregarContratos();
            setFormData(FORM_VAZIO);
        } catch (err) {
            setErro(err.response?.data?.error || err.message);
        } finally {
            setCarregando(false);
        }
    };

    const cancelarContrato = async (cod) => {
        if (!window.confirm("Cancelar este contrato?")) return;
        try {
            await api.put(`/contratos/${cod}/cancelar`);
            carregarContratos();
            setMostrarInfo(false);
            setContratoSelecionado(null);
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível cancelar o contrato.");
        }
    };

    const addDoc = async (e) => {
        e.preventDefault();
        const dados = new FormData();
        dados.append("documento", e.target.documento.files[0]);
        try {
            setCarregando(true);
            setUploadProgress(0);
            await api.post(`/contratos/${codSelecionado}/documentos`, dados, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (ev) => setUploadProgress(Math.round((ev.loaded * 100) / ev.total))
            });
            const r = await api.get(`/contratos/${codSelecionado}`);
            setDetalhes((d) => ({ ...d, documentos: r.data.documentos }));
            setMostrarAddDoc(false);
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível enviar o documento.");
        } finally {
            setCarregando(false);
            setUploadProgress(0);
        }
    };

    const baixarDocumento = async (documento) => {
        try {
            const response = await api.get(`/contratos/documentos/${documento.cod_doc}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = documento.nome;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            alert("Não foi possível baixar o documento.");
        }
    };

    const excluirDocumento = async (cod_doc) => {
        if (!window.confirm("Excluir este documento?")) return;
        try {
            await api.delete(`/contratos/documentos/${cod_doc}`);
            const r = await api.get(`/contratos/${codSelecionado}`);
            setDetalhes((d) => ({ ...d, documentos: r.data.documentos }));
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível excluir o documento.");
        }
    };

    const abrirContrato = (contrato) => {
        setContratoSelecionado(contrato);
        setCodSelecionado(contrato.cod_contrato);
        setMostrarInfo(true);
    };

    const contratosFiltrados = contratos.filter((c) =>
        (c.nome_cliente || "").toLowerCase().includes(busca.toLowerCase()) ||
        (c.status_contrato || "").toLowerCase().includes(busca.toLowerCase()) ||
        (c.tipo_servico || "").toLowerCase().includes(busca.toLowerCase()) ||
        (c.advogados || "").toLowerCase().includes(busca.toLowerCase())
    );

    return (
        <div>
            <Header />

            <main className="page">
                <div className="toolbar">
                    <h2 style={{ margin: 0 }}>Contratos</h2>
                    <div className="toolbar-spacer">
                        <Input placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} />
                        {podeEditar && <Button onClick={abrirNovo}>Novo contrato</Button>}
                    </div>
                </div>

                <ListaPremium
                    vazio="Nenhum contrato encontrado."
                    itens={contratosFiltrados.map((c) => ({
                        chave: c.cod_contrato,
                        iniciais: iniciaisDe(c.nome_cliente),
                        inicialTom: c.status_contrato === "ganho" ? "success" : c.status_contrato === "perdido" || c.status_contrato === "cancelado" ? "danger" : "accent",
                        titulo: c.nome_cliente,
                        subtitulo: `Contrato ${c.cod_contrato} · ${c.tipo_servico} · ${c.advogados || "sem advogado"}`,
                        status: c.status_contrato,
                        direita: brl(c.valor),
                        aoClicar: () => abrirContrato(c)
                    }))}
                />

                {mostrarInfo && contratoSelecionado && (
                    <Modal titulo={`Contrato ${contratoSelecionado.cod_contrato}`} largo onFechar={() => setMostrarInfo(false)}>
                        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 20 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                <div className="grid-form">
                                    <Field label="Cliente"><Input value={contratoSelecionado.nome_cliente} readOnly /></Field>
                                    <Field label="CPF"><Input value={contratoSelecionado.CPF} readOnly /></Field>
                                    <Field label="Tipo de serviço"><Input value={contratoSelecionado.tipo_servico} readOnly /></Field>
                                    <Field label="Status"><Input value={contratoSelecionado.status_contrato} readOnly /></Field>
                                    <Field label="Valor"><Input value={brl(contratoSelecionado.valor)} readOnly /></Field>
                                    <Field label="Início"><Input value={dataBR(contratoSelecionado.data_inicio)} readOnly /></Field>
                                    <Field label="Advogados" className="span-2">
                                        <Textarea readOnly value={detalhes.advogados.map((a) => `${a.nome}${a.OAB ? " (" + a.OAB + ")" : ""}`).join("\n") || "—"} />
                                    </Field>
                                    <Field label="Descrição" className="span-2">
                                        <Textarea readOnly value={contratoSelecionado.descricao || ""} />
                                    </Field>
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <span className="card-kicker">Documentos</span>
                                        {podeEditar && <Button variante="ghost" onClick={() => { setMostrarAddDoc(true); setMostrarInfo(false); }}>+ Adicionar</Button>}
                                    </div>
                                    <ul className="mini-lista">
                                        {detalhes.documentos.map((d) => (
                                            <li key={d.cod_doc}>
                                                <button className="btn btn-ghost" style={{ padding: 0 }} onClick={() => baixarDocumento(d)}>{d.nome}</button>
                                                {podeEditar && <Button variante="danger" style={{ marginLeft: "auto" }} onClick={() => excluirDocumento(d.cod_doc)}>✕</Button>}
                                            </li>
                                        ))}
                                        {detalhes.documentos.length === 0 && <li className="text-muted">Nenhum documento.</li>}
                                    </ul>
                                </div>

                                <div>
                                    <span className="card-kicker">Pagamentos</span>
                                    <ul className="mini-lista">
                                        {detalhes.pagamentos.map((p) => (
                                            <li key={p.cod_pag}>{brl(p.valorPago)} · {p.metodo} <Tag tom={tomDoStatus(p.status_pag)}>{p.status_pag}</Tag></li>
                                        ))}
                                        {detalhes.pagamentos.length === 0 && <li className="text-muted">Nenhum pagamento.</li>}
                                    </ul>
                                </div>

                                <div>
                                    <span className="card-kicker">Compromissos</span>
                                    <ul className="mini-lista">
                                        {detalhes.compromissos.map((a) => (
                                            <li key={a.cod_compromisso}>{a.nome_compromisso} · {dataHoraBR(a.data_compromisso)}</li>
                                        ))}
                                        {detalhes.compromissos.length === 0 && <li className="text-muted">Nenhum compromisso.</li>}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="modal-acoes">
                            {podeEditar && <Button variante="danger" onClick={() => cancelarContrato(contratoSelecionado.cod_contrato)}>Cancelar contrato</Button>}
                            <Button variante="secondary" onClick={() => setMostrarInfo(false)}>Voltar</Button>
                            {podeEditar && <Button onClick={abrirEditar}>Editar</Button>}
                        </div>
                    </Modal>
                )}

                {mostrarForm && (
                    <Modal titulo={editando ? "Editar contrato" : "Novo contrato"} largo onFechar={() => setMostrarForm(false)}>
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div className="grid-form">
                                <Field label="Cliente">
                                    <Select name="id_cliente" required value={formData.id_cliente} onChange={handleChange}>
                                        <option value="">Selecione o cliente</option>
                                        {clientes.map((c) => <option key={c.id_cliente} value={c.id_cliente}>{c.nome}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Data de início">
                                    <Input type="date" name="data_inicio" required value={formData.data_inicio} onChange={handleChange} />
                                </Field>
                                <Field label="Tipo de serviço">
                                    <Select name="tipo_servico" required value={formData.tipo_servico} onChange={handleChange}>
                                        <option value="">Selecione o tipo</option>
                                        {TIPOS_SERVICO.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Status">
                                    <Select name="status_contrato" required value={formData.status_contrato} onChange={handleChange}>
                                        {STATUS_CONTRATO.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Valor (R$)">
                                    <Input name="valor" required value={formData.valor} onChange={handleChange} placeholder="0,00" />
                                </Field>
                                <div className="field">
                                    <label>Advogados do contrato</label>
                                    <div className="check-lista">
                                        {equipe.map((adv) => (
                                            <label key={adv.id_usuario}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.ids_advogados.includes(adv.id_usuario)}
                                                    onChange={() => toggleAdvogado(adv.id_usuario)}
                                                />
                                                {adv.nome} {adv.OAB ? `(${adv.OAB})` : ""}
                                            </label>
                                        ))}
                                        {equipe.length === 0 && <span className="text-muted">Nenhum advogado disponível.</span>}
                                    </div>
                                </div>
                                <Field label="Descrição" className="span-2">
                                    <Textarea name="descricao" value={formData.descricao} onChange={handleChange} />
                                </Field>
                            </div>
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => setMostrarForm(false)}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Salvando..." : "Salvar"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}

                {mostrarAddDoc && (
                    <Modal titulo="Adicionar documento" onFechar={() => setMostrarAddDoc(false)}>
                        <form onSubmit={addDoc} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <Field label="Arquivo">
                                <Input type="file" name="documento" required />
                            </Field>
                            <div className="modal-acoes">
                                {uploadProgress > 0 && <span className="text-muted">{uploadProgress}%</span>}
                                <Button variante="secondary" onClick={() => { setMostrarAddDoc(false); setMostrarInfo(true); }}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Enviando..." : "Enviar"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}
            </main>
        </div>
    );
};

export default Contratos;
